import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Store } from '../../Store';
import { api } from '../../Api';
import { toast } from 'react-toastify';

// ─── Mini SVG Line Chart ────────────────────────────────────────
function MiniChart({ data, color = '#00ff88', height = 60, width = '100%', fill = true, maxVal }) {
  if (!data || data.length < 2) return <div style={{ height, opacity: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 11 }}>Collecting data...</div>;
  const max = maxVal || Math.max(...data, 1);
  const min = 0;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((v - min) / (max - min)) * 100;
    return `${x},${y}`;
  });
  const line = points.join(' ');
  const area = `0,100 ${line} 100,100`;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width, height, display: 'block' }}>
      {fill && <polygon points={area} fill={color} fillOpacity="0.15" />}
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ─── Circular Gauge ─────────────────────────────────────────────
function CircleGauge({ value, max = 100, label, unit, color = '#00ff88', size = 120 }) {
  const pct = Math.min((value / max) * 100, 100);
  const r = 44;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#1e293b" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        <text x="50" y="46" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">{value}</text>
        <text x="50" y="60" textAnchor="middle" fill="#94a3b8" fontSize="8">{unit}</text>
      </svg>
      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: -5 }}>{label}</div>
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = '#00ff88' }) {
  return (
    <div style={{ background: '#0f172a', borderRadius: 12, padding: '16px 20px', border: '1px solid #1e293b', flex: 1, minWidth: 150 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <i className={`fa ${icon}`} style={{ color, fontSize: 14 }} />
        <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 'bold', color: 'white' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function SystemMonitorScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = { Authorization: `Bearer ${userInfo.token}` };

  // Server stats
  const [stats, setStats] = useState(null);
  // Speed
  const [speedData, setSpeedData] = useState({ dl: null, ul: null, ping: null, testing: false });
  const [speedHistory, setSpeedHistory] = useState([]);
  // Sessions
  const [sessions, setSessions] = useState([]);
  // Logs
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  // PM2 Logs
  const [pm2Out, setPm2Out] = useState([]);
  const [pm2Err, setPm2Err] = useState([]);
  const pm2OutRef = useRef(null);
  const pm2ErrRef = useRef(null);
  // PM2 Instances
  const [pm2Instances, setPm2Instances] = useState([]);
  const [pm2InstancesError, setPm2InstancesError] = useState('');
  const [pm2HiddenCount, setPm2HiddenCount] = useState(0);
  const [pm2RestartingId, setPm2RestartingId] = useState(null);
  const [pm2ReloadingAll, setPm2ReloadingAll] = useState(false);
  // Clock
  const [now, setNow] = useState(new Date());

  // ─── Fetch server stats ──────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(api + '/api/admin/server-stats', { headers });
      setStats(data);
    } catch {}
  }, []);

  // ─── Fetch sessions ──────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(api + '/api/admin/active-sessions', { headers });
      setSessions(data.sessions);
    } catch {}
  }, []);

  // ─── Fetch logs ──────────────────────────────────────────────
  const fetchLogs = useCallback(async (page = 1) => {
    try {
      const { data } = await axios.get(api + `/api/admin/request-logs?page=${page}&limit=10`, { headers });
      setLogs(data.logs);
      setLogsTotal(data.total);
      setLogsPage(data.page);
    } catch {}
  }, []);

  // ─── Fetch PM2 logs ───────────────────────────────────────────
  const fetchPm2Logs = useCallback(async () => {
    try {
      const { data } = await axios.get(api + '/api/admin/pm2-logs?lines=100', { headers });
      setPm2Out(data.out);
      setPm2Err(data.err);
      setTimeout(() => {
        if (pm2OutRef.current) pm2OutRef.current.scrollTop = pm2OutRef.current.scrollHeight;
        if (pm2ErrRef.current) pm2ErrRef.current.scrollTop = pm2ErrRef.current.scrollHeight;
      }, 50);
    } catch {}
  }, []);

  // ─── Fetch PM2 instances ─────────────────────────────────────
  const fetchPm2Instances = useCallback(async () => {
    try {
      const { data } = await axios.get(api + '/api/admin/pm2-instances', { headers });
      setPm2Instances(data.instances || []);
      setPm2HiddenCount(data.hiddenCount || 0);
      setPm2InstancesError('');
    } catch (err) {
      setPm2InstancesError(err.response?.data?.message || 'PM2 unavailable');
      setPm2Instances([]);
      setPm2HiddenCount(0);
    }
  }, []);

  const restartPm2Instance = async (inst) => {
    const target = inst.pm_id != null ? inst.pm_id : inst.name;
    if (!window.confirm(`Reload instance #${inst.pm_id} (${inst.name})? This is zero-downtime in cluster mode.`)) return;
    try {
      setPm2RestartingId(target);
      await axios.post(api + `/api/admin/pm2-restart/${target}?mode=reload`, {}, { headers });
      toast.success(`Reload queued for ${inst.name} #${inst.pm_id}`);
      // Refresh after a short delay so the new uptime shows up.
      setTimeout(fetchPm2Instances, 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reload failed');
    } finally {
      setTimeout(() => setPm2RestartingId(null), 2500);
    }
  };

  const hardRestartPm2Instance = async (inst) => {
    const target = inst.pm_id != null ? inst.pm_id : inst.name;
    if (!window.confirm(`Hard-restart instance #${inst.pm_id} (${inst.name})?\nThis kills the worker — in-flight requests on it will fail.`)) return;
    try {
      setPm2RestartingId(target);
      await axios.post(api + `/api/admin/pm2-restart/${target}?mode=restart`, {}, { headers });
      toast.success(`Restart queued for ${inst.name} #${inst.pm_id}`);
      setTimeout(fetchPm2Instances, 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Restart failed');
    } finally {
      setTimeout(() => setPm2RestartingId(null), 2500);
    }
  };

  const reloadAllPm2 = async () => {
    if (!window.confirm('Rolling-reload all 4 backend workers? Zero downtime.')) return;
    try {
      setPm2ReloadingAll(true);
      await axios.post(api + '/api/admin/pm2-reload-all?app=clickcart-backend', {}, { headers });
      toast.success('Rolling reload queued');
      setTimeout(fetchPm2Instances, 3000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reload failed');
    } finally {
      setTimeout(() => setPm2ReloadingAll(false), 3000);
    }
  };

  // ─── Speed test ──────────────────────────────────────────────
  const runSpeedTest = useCallback(async () => {
    setSpeedData(prev => ({ ...prev, testing: true }));
    try {
      // Ping
      let totalPing = 0;
      for (let i = 0; i < 3; i++) {
        const s = performance.now();
        await axios.post(api + '/api/admin/speed-test/ping', {}, { headers });
        totalPing += performance.now() - s;
      }
      const avgPing = Math.round(totalPing / 3);

      // Download
      const dlStart = performance.now();
      const dlRes = await axios.get(api + '/api/admin/speed-test', { headers, responseType: 'arraybuffer' });
      const dlTime = (performance.now() - dlStart) / 1000;
      const dlSpeed = ((dlRes.data.byteLength * 8) / dlTime / 1024 / 1024).toFixed(2);

      // Upload
      const ulStart = performance.now();
      await axios.post(api + '/api/admin/speed-test/ping', new ArrayBuffer(200 * 1024), {
        headers: { ...headers, 'Content-Type': 'application/octet-stream' },
      });
      const ulTime = (performance.now() - ulStart) / 1000;
      const ulSpeed = ((200 * 1024 * 8) / ulTime / 1024 / 1024).toFixed(2);

      setSpeedData({ dl: dlSpeed, ul: ulSpeed, ping: avgPing, testing: false });
      setSpeedHistory(prev => [...prev.slice(-29), { dl: parseFloat(dlSpeed), ul: parseFloat(ulSpeed), ping: avgPing, t: Date.now() }]);
    } catch {
      setSpeedData(prev => ({ ...prev, testing: false }));
    }
  }, []);

  const clearLogs = async () => {
    if (!window.confirm('Clear all request logs?')) return;
    await axios.delete(api + '/api/admin/request-logs', { headers }).catch(() => {});
    toast.success('Logs cleared');
    fetchLogs(1);
  };

  const clearSessions = async () => {
    if (!window.confirm('Clear all sessions?')) return;
    await axios.delete(api + '/api/admin/active-sessions', { headers }).catch(() => {});
    toast.success('Sessions cleared');
    fetchSessions();
  };

  // ─── Lifecycle ───────────────────────────────────────────────
  // Polls only while the tab is visible. When hidden (minimized, other tab),
  // intervals are torn down so the server isn't hammered for nothing.
  // Speed test is manual only (button below) to avoid burning bandwidth.
  useEffect(() => {
    let active = true;
    let intervals = [];

    const start = () => {
      if (intervals.length) return; // already running
      // Initial fetches
      fetchStats(); fetchSessions(); fetchLogs(1); fetchPm2Logs(); fetchPm2Instances();
      intervals.push(
        setInterval(() => { if (active) fetchStats(); }, 5000),
        setInterval(() => { if (active) fetchSessions(); }, 15000),
        setInterval(() => { if (active) fetchLogs(1); }, 20000),
        setInterval(() => { if (active) fetchPm2Logs(); }, 15000),
        setInterval(() => { if (active) fetchPm2Instances(); }, 5000),
      );
    };

    const stop = () => {
      intervals.forEach(clearInterval);
      intervals = [];
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    const clockIv = setInterval(() => setNow(new Date()), 1000);

    return () => {
      active = false;
      stop();
      clearInterval(clockIv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // ─── Helpers ─────────────────────────────────────────────────
  const fmt = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
  };

  const onlineCount = sessions.filter(s => (Date.now() - new Date(s.lastActive).getTime()) < 120000).length;
  const idleCount = sessions.length - onlineCount;

  // Group sessions by country
  const countryMap = {};
  sessions.forEach(s => {
    const c = s.country || 'Unknown';
    countryMap[c] = (countryMap[c] || 0) + 1;
  });
  const countries = Object.entries(countryMap).sort((a, b) => b[1] - a[1]);

  const bg = '#020617';
  const card = '#0f172a';
  const border = '#1e293b';

  if (!stats) {
    return (
      <div style={{ width: '87%', float: 'right', background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner animation="border" variant="light" />
      </div>
    );
  }

  return (
    <div style={{ width: '87%', float: 'right', background: bg, minHeight: '100vh', padding: '20px 24px', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <Helmet><title>System Monitor</title></Helmet>

      {/* ═══ HEADER ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h4 style={{ color: 'white', margin: 0, fontWeight: 700 }}>
            <i className="fa fa-server" style={{ color: '#00ff88', marginRight: 10 }} />
            System Monitor
          </h4>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
            {stats.server.hostname} | {stats.server.cpuModel.split('@')[0].trim()} | {stats.server.cpuCount} Cores | Node {stats.server.nodeVersion}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#00ff88', fontSize: 24, fontWeight: 700, fontFamily: 'monospace' }}>
            {now.toLocaleTimeString()}
          </div>
          <div style={{ color: '#64748b', fontSize: 11 }}>{now.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
      </div>

      {/* ═══ ROW 1: Key Metrics ═══ */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard icon="fa-clock-o" label="Uptime" value={fmt(stats.server.uptime)} sub={`Server: ${fmt(stats.server.serverUptime)}`} color="#38bdf8" />
        <StatCard icon="fa-exchange" label="Total Requests" value={stats.requests.total.toLocaleString()} sub={`${stats.requests.errors} errors (${stats.requests.errorRate}%)`} color="#a78bfa" />
        <StatCard icon="fa-bolt" label="Active Now" value={stats.requests.active} sub="In-flight requests" color="#fbbf24" />
        <StatCard icon="fa-users" label="Online Users" value={onlineCount} sub={`${idleCount} idle | ${sessions.length} total`} color="#34d399" />
        <StatCard icon="fa-wifi" label="Ping" value={speedData.ping !== null ? `${speedData.ping}ms` : '--'} sub={speedData.testing ? 'Testing...' : 'To server'} color={speedData.ping && speedData.ping < 100 ? '#00ff88' : '#f59e0b'} />
      </div>

      {/* ═══ ROW 2: CPU / RAM / Speed Gauges + Charts ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>

        {/* CPU */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            <i className="fa fa-microchip" style={{ color: '#38bdf8', marginRight: 6 }} /> CPU Usage
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <CircleGauge
              value={stats.charts.cpu.length > 0 ? stats.charts.cpu[stats.charts.cpu.length - 1] : 0}
              label="Current" unit="%" color="#38bdf8" size={100}
            />
            <div style={{ flex: 1 }}>
              <MiniChart data={stats.charts.cpu} color="#38bdf8" height={70} />
              <div style={{ color: '#475569', fontSize: 10, display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span>60s ago</span><span>now</span>
              </div>
            </div>
          </div>
          <div style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
            Load: {stats.server.loadAvg.join(' / ')}
          </div>
        </div>

        {/* RAM */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            <i className="fa fa-database" style={{ color: '#a78bfa', marginRight: 6 }} /> Memory Usage
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <CircleGauge
              value={stats.memory.system.percent} label="System" unit={`${stats.memory.system.used}MB`}
              color="#a78bfa" size={100}
            />
            <div style={{ flex: 1 }}>
              <MiniChart data={stats.charts.mem.map(m => m.percent)} color="#a78bfa" height={70} />
              <div style={{ color: '#475569', fontSize: 10, display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span>60s ago</span><span>now</span>
              </div>
            </div>
          </div>
          <div style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
            Process: {stats.memory.process.rss}MB RSS | Heap: {stats.memory.process.heapUsed}/{stats.memory.process.heapTotal}MB
          </div>
        </div>

        {/* Network Speed */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              <i className="fa fa-wifi" style={{ color: '#34d399', marginRight: 6 }} /> Network Speed
            </span>
            {speedData.testing ? (
              <Spinner size="sm" animation="border" variant="success" />
            ) : (
              <Button
                variant="outline-success"
                size="sm"
                onClick={runSpeedTest}
                style={{ fontSize: 10, padding: '2px 8px' }}
              >
                Run Test
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 'bold', color: '#34d399' }}>
                {speedData.dl || '--'}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>Download Mbps</div>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fbbf24', marginTop: 4 }}>
                {speedData.ul || '--'}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>Upload Mbps</div>
            </div>
            <div style={{ flex: 1 }}>
              <MiniChart data={speedHistory.map(s => s.dl)} color="#34d399" height={30} />
              <MiniChart data={speedHistory.map(s => s.ul)} color="#fbbf24" height={30} />
              <div style={{ color: '#475569', fontSize: 10, display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span>oldest</span><span>latest</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ PM2 CLUSTER WORKERS ═══ */}
      <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
          <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
            <i className="fa fa-cubes" style={{ color: '#22d3ee', marginRight: 6 }} /> PM2 Cluster Workers
            <Badge bg="info" className="ms-2" style={{ fontSize: 10 }}>{pm2Instances.length} processes</Badge>
            {pm2HiddenCount > 0 && (
              <Badge bg="dark" className="ms-1" style={{ fontSize: 10 }} title="Non-ClickCart/Icon PM2 processes on this server are hidden">
                {pm2HiddenCount} hidden
              </Badge>
            )}
          </span>
          <Button
            variant="outline-info"
            size="sm"
            onClick={reloadAllPm2}
            disabled={pm2ReloadingAll || !pm2Instances.length}
            style={{ fontSize: 10, padding: '2px 10px' }}
          >
            {pm2ReloadingAll ? <Spinner size="sm" animation="border" /> : <><i className="fa fa-refresh" /> Rolling Reload All</>}
          </Button>
        </div>
        {pm2InstancesError ? (
          <div style={{ padding: 16, color: '#ef4444', fontSize: 13 }}>
            <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />
            {pm2InstancesError} — make sure pm2 is on PATH for the backend process.
          </div>
        ) : pm2Instances.length === 0 ? (
          <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>Loading workers…</div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <Table size="sm" className="mb-0" style={{ color: '#cbd5e1', fontSize: 12 }}>
              <thead style={{ background: '#1e293b' }}>
                <tr>
                  <th style={{ color: '#94a3b8', border: 'none' }}>#</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Name</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>PID</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Mode</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Status</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Uptime</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>CPU</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Memory</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Restarts</th>
                  <th style={{ color: '#94a3b8', border: 'none', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pm2Instances.map((inst) => {
                  const statusColor =
                    inst.status === 'online' ? '#22c55e' :
                    inst.status === 'launching' ? '#fbbf24' :
                    inst.status === 'errored' || inst.status === 'stopped' ? '#ef4444' : '#94a3b8';
                  const cpuColor = inst.cpu > 80 ? '#ef4444' : inst.cpu > 50 ? '#fbbf24' : '#22c55e';
                  const memColor = inst.memoryMB > 500 ? '#ef4444' : inst.memoryMB > 300 ? '#fbbf24' : '#22c55e';
                  const target = inst.pm_id != null ? inst.pm_id : inst.name;
                  const isRestarting = pm2RestartingId === target;
                  return (
                    <tr key={`${inst.name}-${inst.pm_id}`}>
                      <td style={{ border: 'none', color: '#22d3ee', fontWeight: 600 }}>{inst.pm_id}</td>
                      <td style={{ border: 'none' }}>
                        {inst.name}
                        {inst.instance != null && (
                          <span style={{ color: '#64748b', fontSize: 10, marginLeft: 6 }}>worker {inst.instance}</span>
                        )}
                      </td>
                      <td style={{ border: 'none', color: '#94a3b8', fontFamily: 'monospace' }}>{inst.pid || '—'}</td>
                      <td style={{ border: 'none' }}>
                        <Badge bg={inst.execMode === 'cluster_mode' ? 'info' : 'secondary'} style={{ fontSize: 10 }}>
                          {inst.execMode === 'cluster_mode' ? 'cluster' : 'fork'}
                        </Badge>
                      </td>
                      <td style={{ border: 'none' }}>
                        <span style={{ color: statusColor, fontWeight: 600 }}>
                          <i className="fa fa-circle" style={{ fontSize: 8, marginRight: 5 }} />
                          {inst.status}
                        </span>
                      </td>
                      <td style={{ border: 'none' }}>
                        {inst.status === 'online' ? fmt(Math.floor(inst.uptimeMs / 1000)) : '—'}
                      </td>
                      <td style={{ border: 'none', color: cpuColor, fontWeight: 600 }}>{inst.cpu}%</td>
                      <td style={{ border: 'none', color: memColor, fontWeight: 600 }}>{inst.memoryMB} MB</td>
                      <td style={{ border: 'none' }}>
                        {inst.restarts}
                        {inst.unstableRestarts > 0 && (
                          <span style={{ color: '#ef4444', marginLeft: 4 }} title="unstable restarts">
                            ⚠ {inst.unstableRestarts}
                          </span>
                        )}
                      </td>
                      <td style={{ border: 'none', textAlign: 'right' }}>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => restartPm2Instance(inst)}
                          disabled={isRestarting}
                          style={{ fontSize: 10, padding: '2px 8px', marginRight: 4 }}
                          title="Zero-downtime reload"
                        >
                          {isRestarting ? <Spinner size="sm" animation="border" /> : <><i className="fa fa-refresh" /> Reload</>}
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => hardRestartPm2Instance(inst)}
                          disabled={isRestarting}
                          style={{ fontSize: 10, padding: '2px 8px' }}
                          title="Hard restart"
                        >
                          <i className="fa fa-power-off" /> Restart
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* ═══ ROW 3: Request Rate + Status Codes + Top Endpoints ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 16 }}>

        {/* Request Rate Chart */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            <i className="fa fa-line-chart" style={{ color: '#fb923c', marginRight: 6 }} /> Requests / Second
          </div>
          <MiniChart data={stats.charts.requestRate} color="#fb923c" height={80} />
          <div style={{ color: '#475569', fontSize: 10, display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span>60s ago</span>
            <span>Current: {stats.charts.requestRate.length > 0 ? stats.charts.requestRate[stats.charts.requestRate.length - 1] : 0} req/s</span>
          </div>
        </div>

        {/* Status Codes */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
            <i className="fa fa-pie-chart" style={{ color: '#e879f9', marginRight: 6 }} /> Response Codes
          </div>
          {[
            { label: '2xx Success', count: stats.requests.statusCodes['2xx'], color: '#22c55e' },
            { label: '3xx Redirect', count: stats.requests.statusCodes['3xx'], color: '#3b82f6' },
            { label: '4xx Client Error', count: stats.requests.statusCodes['4xx'], color: '#f59e0b' },
            { label: '5xx Server Error', count: stats.requests.statusCodes['5xx'], color: '#ef4444' },
          ].map(s => {
            const pct = stats.requests.total > 0 ? (s.count / stats.requests.total * 100) : 0;
            return (
              <div key={s.label} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>
                  <span>{s.label}</span><span style={{ color: 'white' }}>{s.count}</span>
                </div>
                <div style={{ height: 6, background: '#1e293b', borderRadius: 3 }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: s.color, borderRadius: 3, transition: 'width 0.5s' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Top Endpoints */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            <i className="fa fa-fire" style={{ color: '#f97316', marginRight: 6 }} /> Top Endpoints
          </div>
          <div style={{ maxHeight: 150, overflow: 'auto' }}>
            {stats.requests.topEndpoints.map((ep, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #1e293b', fontSize: 11 }}>
                <span style={{ color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{ep.endpoint}</span>
                <span style={{ color: '#f97316', fontWeight: 'bold' }}>{ep.hits}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ ROW 4: Live Users Table + Users by Country ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 12, marginBottom: 16 }}>

        {/* Live Users */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
            <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              <i className="fa fa-users" style={{ color: '#34d399', marginRight: 6 }} /> Live Users
              <Badge bg="success" className="ms-2" style={{ fontSize: 10 }}>{onlineCount} online</Badge>
            </span>
            <Button variant="outline-danger" size="sm" onClick={clearSessions} style={{ fontSize: 10, padding: '2px 8px' }}>
              <i className="fa fa-trash" /> Clear All
            </Button>
          </div>
          <div style={{ maxHeight: 250, overflow: 'auto' }}>
            <Table size="sm" className="mb-0" style={{ color: '#cbd5e1', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#1e293b' }}>
                <tr>
                  <th style={{ color: '#94a3b8', border: 'none' }}>#</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>User</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>IP</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Location</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Device</th>
                  <th style={{ color: '#94a3b8', border: 'none' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#475569', border: 'none' }}>No active sessions</td></tr>
                ) : sessions.map((s, i) => {
                  const isOn = (Date.now() - new Date(s.lastActive).getTime()) < 120000;
                  return (
                    <tr key={s._id} style={{ borderColor: '#1e293b' }}>
                      <td style={{ borderColor: '#1e293b' }}>{i + 1}</td>
                      <td style={{ borderColor: '#1e293b' }}><strong>{s.username}</strong></td>
                      <td style={{ borderColor: '#1e293b', fontFamily: 'monospace', fontSize: 11 }}>{s.ip}</td>
                      <td style={{ borderColor: '#1e293b' }}>{s.city && s.country ? `${s.city}, ${s.country}` : '-'}</td>
                      <td style={{ borderColor: '#1e293b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.userAgent ? s.userAgent.split('(')[0].trim() : '-'}</td>
                      <td style={{ borderColor: '#1e293b' }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: isOn ? '#22c55e' : '#475569', marginRight: 4, boxShadow: isOn ? '0 0 6px #22c55e' : 'none' }} />
                        {isOn ? 'Online' : 'Idle'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Users by Country */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
            <i className="fa fa-globe" style={{ color: '#38bdf8', marginRight: 6 }} /> Users by Country
          </div>
          {countries.length === 0 ? (
            <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: 20 }}>No data</div>
          ) : (
            countries.map(([country, count]) => {
              const pct = (count / sessions.length) * 100;
              return (
                <div key={country} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                    <span style={{ color: '#cbd5e1' }}>{country}</span>
                    <span style={{ color: '#38bdf8', fontWeight: 'bold' }}>{count}</span>
                  </div>
                  <div style={{ height: 6, background: '#1e293b', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#38bdf8', borderRadius: 3, minWidth: 4, transition: 'width 0.5s' }} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ═══ ROW 5: Failed Request Logs ═══ */}
      <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
          <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
            <i className="fa fa-exclamation-triangle" style={{ color: '#ef4444', marginRight: 6 }} /> Failed Requests
            <Badge bg="danger" className="ms-2" style={{ fontSize: 10 }}>{logsTotal}</Badge>
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button variant="outline-light" size="sm" onClick={() => fetchLogs(1)} style={{ fontSize: 10, padding: '2px 8px' }}>
              <i className="fa fa-refresh" />
            </Button>
            <Button variant="outline-danger" size="sm" onClick={clearLogs} style={{ fontSize: 10, padding: '2px 8px' }}>
              <i className="fa fa-trash" /> Clear
            </Button>
          </div>
        </div>
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          <Table size="sm" className="mb-0" style={{ color: '#cbd5e1', fontSize: 11 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#1e293b' }}>
              <tr>
                <th style={{ color: '#94a3b8', border: 'none' }}>Time</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>Code</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>Method</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>Endpoint</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>Error</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>Cause</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>IP</th>
                <th style={{ color: '#94a3b8', border: 'none' }}>ms</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#475569', border: 'none' }}>No failed requests</td></tr>
              ) : logs.map(log => (
                <tr key={log._id} style={{ borderColor: '#1e293b' }}>
                  <td style={{ whiteSpace: 'nowrap', borderColor: '#1e293b' }}>{new Date(log.createdAt).toLocaleTimeString()}</td>
                  <td style={{ borderColor: '#1e293b' }}>
                    <span style={{ color: log.statusCode >= 500 ? '#ef4444' : log.statusCode === 408 ? '#f59e0b' : '#94a3b8', fontWeight: 'bold' }}>{log.statusCode}</span>
                  </td>
                  <td style={{ borderColor: '#1e293b' }}><Badge bg="dark" style={{ fontSize: 10 }}>{log.method}</Badge></td>
                  <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderColor: '#1e293b', fontFamily: 'monospace' }}>{log.endpoint}</td>
                  <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderColor: '#1e293b' }}>{log.errorMessage}</td>
                  <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderColor: '#1e293b', color: '#64748b' }}>{log.possibleCause}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 10, borderColor: '#1e293b' }}>{log.ip}</td>
                  <td style={{ borderColor: '#1e293b' }}>{log.duration}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {logsTotal > 10 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 8, gap: 8, borderTop: `1px solid ${border}` }}>
            <Button size="sm" variant="outline-light" disabled={logsPage <= 1} onClick={() => fetchLogs(logsPage - 1)} style={{ fontSize: 10 }}>Prev</Button>
            <span style={{ color: '#94a3b8', fontSize: 11, alignSelf: 'center' }}>Page {logsPage}/{Math.ceil(logsTotal / 10)}</span>
            <Button size="sm" variant="outline-light" disabled={logsPage >= Math.ceil(logsTotal / 10)} onClick={() => fetchLogs(logsPage + 1)} style={{ fontSize: 10 }}>Next</Button>
          </div>
        )}
      </div>

      {/* ═══ ROW 6: PM2 Live Logs ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {/* stdout */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${border}` }}>
            <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              <i className="fa fa-terminal" style={{ color: '#34d399', marginRight: 6 }} /> PM2 Output Logs
              <span style={{ marginLeft: 8, width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', boxShadow: '0 0 6px #22c55e' }} />
            </span>
            <span style={{ color: '#475569', fontSize: 10 }}>Auto-refresh 15s</span>
          </div>
          <div
            ref={pm2OutRef}
            style={{
              height: 250,
              overflow: 'auto',
              padding: '8px 12px',
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: 11,
              lineHeight: 1.6,
              background: '#020617',
            }}
          >
            {pm2Out.length === 0 ? (
              <div style={{ color: '#475569', textAlign: 'center', paddingTop: 80 }}>No logs yet...</div>
            ) : pm2Out.map((line, i) => (
              <div key={i} style={{ color: line.includes('error') || line.includes('Error') ? '#ef4444' : line.includes('Connected') ? '#22c55e' : '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                <span style={{ color: '#334155', marginRight: 8, userSelect: 'none' }}>{pm2Out.length - pm2Out.length + i + 1}</span>
                {line}
              </div>
            ))}
          </div>
        </div>

        {/* stderr */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${border}` }}>
            <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              <i className="fa fa-exclamation-circle" style={{ color: '#ef4444', marginRight: 6 }} /> PM2 Error Logs
              {pm2Err.length > 0 && <Badge bg="danger" className="ms-2" style={{ fontSize: 9 }}>{pm2Err.length}</Badge>}
            </span>
            <span style={{ color: '#475569', fontSize: 10 }}>Auto-refresh 15s</span>
          </div>
          <div
            ref={pm2ErrRef}
            style={{
              height: 250,
              overflow: 'auto',
              padding: '8px 12px',
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: 11,
              lineHeight: 1.6,
              background: '#0c0a1a',
            }}
          >
            {pm2Err.length === 0 ? (
              <div style={{ color: '#475569', textAlign: 'center', paddingTop: 80 }}>No errors</div>
            ) : pm2Err.map((line, i) => (
              <div key={i} style={{ color: line.includes('WARN') ? '#f59e0b' : '#ef4444', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                <span style={{ color: '#334155', marginRight: 8, userSelect: 'none' }}>{i + 1}</span>
                {line}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', color: '#334155', fontSize: 11, padding: '10px 0' }}>
        ClickCart System Monitor | Stats 5s | Sessions 15s | Logs 20s | PM2 15s | Speed: manual | Pauses when tab hidden
      </div>
    </div>
  );
}
